import * as _ from "underscore";

import * as utils from "./utils.js";

/**
 * @type {{
 *   [key: string]: {
 *     name: string,
 *     english: string,
 *     ttsVoiceSelectedText: string,
 *     frequencies?: true,
 *     redditUrl?: string,
 *     dictionaryURL?: string,
 *     dictionaryCodes?: { [languageCode: string]: string },
 *     capitalizationImportant?: true,
 *     monolingualDictionaryURL?: string,
 *     wiktionary?: boolean,
 *     alphabet?: string,
 *     direction?: "RTL"
 *     noWordBoundaries?: true,
 *     firstLanguageOnly?: true,
 *     beta?: true,
 *     starterStories?: {easy: string, medium: string}
 *     flag: string,
 *     transliterations?: string[],
 *   }
 * }}
 */
export const map = {
  en: {
    name: "English",
    english: "English",
    frequencies: true,
    monolingualDictionaryURL: "https://en.m.wiktionary.org/wiki/{{query}}",
    wiktionary: true,
    starterStories: {
      easy: "648c8d13e6ebfa9964a44c2e",
      medium: "6488534a2d6cce87eb44569b",
    },
    flag: "🇺🇸",
    ttsVoiceSelectedText: "Hello, you have selected the voice {{voiceName}}.",
  },
  es: {
    name: "Español",
    english: "Spanish",
    frequencies: true,
    redditUrl:
      "http://www.reddit.com/r/ReadingSpanish/submit?url={{publicUrl}}&title={{title}}",
    monolingualDictionaryURL: "https://es.wiktionary.org/wiki/{{query}}",
    wiktionary: true,
    starterStories: {
      easy: "648c8c89e6ebfa9964a40adf",
      medium: "6488535a2d6cce87eb4458f9",
    },
    flag: "🇪🇸",
    ttsVoiceSelectedText: "Hola, has seleccionado la voz {{voiceName}}.",
  },
  fr: {
    name: "Français",
    english: "French",
    frequencies: true,
    wiktionary: true,
    starterStories: {
      easy: "648c8caae6ebfa9964a4103c",
      medium: "6488536d2d6cce87eb446f1e",
    },
    flag: "🇫🇷",
    ttsVoiceSelectedText:
      "Bonjour, vous avez sélectionné la voix {{voiceName}}.",
  },
  it: {
    name: "Italiano",
    english: "Italian",
    frequencies: true,
    wiktionary: true,
    starterStories: {
      easy: "648c8ccae6ebfa9964a429f9",
      medium: "6488537c2d6cce87eb447cee",
    },
    flag: "🇮🇹",
    ttsVoiceSelectedText: "Ciao, hai selezionato la voce {{voiceName}}.",
  },
  de: {
    name: "Deutsch",
    english: "German",
    frequencies: true,
    capitalizationImportant: true,
    wiktionary: true,
    starterStories: {
      easy: "648c8c5fe6ebfa9964a4018a",
      medium: "6488533b2d6cce87eb4437cd",
    },
    flag: "🇩🇪",
    ttsVoiceSelectedText:
      "Hallo, Sie haben die Stimme {{voiceName}} ausgewählt.",
  },
  ca: {
    name: "Català",
    english: "Catalan",
    frequencies: true,
    monolingualDictionaryURL:
      "https://dlc.iec.cat/results.asp?txtEntrada={{query}}&operEntrada=0",
    wiktionary: true,
    flag: "🇦🇩",
    ttsVoiceSelectedText: "Hola, has seleccionat la veu {{voiceName}}.",
  },
  pl: {
    name: "Polski",
    english: "Polish",
    frequencies: true,
    alphabet: "latin-ext",
    wiktionary: true,
    flag: "🇵🇱",
    ttsVoiceSelectedText: "Witaj, wybrałeś głos {{voiceName}}.",
  },
  da: {
    name: "Dansk",
    english: "Danish",
    frequencies: true,
    flag: "🇩🇰",
    ttsVoiceSelectedText: "Hej, du har valgt stemmen {{voiceName}}.",
  },
  pt: {
    name: "Português",
    english: "Portuguese",
    frequencies: true,
    wiktionary: true,
    flag: "🇵🇹",
    ttsVoiceSelectedText: "Olá, você selecionou a voz {{voiceName}}.",
  },
  sv: {
    name: "Svenska",
    english: "Swedish",
    frequencies: true,
    dictionaryURL:
      "https://folkets-lexikon.csc.kth.se/folkets/#lookup&{{query}}",
    dictionaryCodes: { en: ".en.", sv: "." },
    wiktionary: true,
    flag: "🇸🇪",
    ttsVoiceSelectedText: "Hej, du har valt rösten {{voiceName}}.",
  },
  sr: {
    name: "Cрпски",
    english: "Serbian",
    frequencies: true,
    alphabet: "cyrillic",
    flag: "🇷🇸",
    ttsVoiceSelectedText: "Здраво, изабрали сте глас {{voiceName}}.",
  },
  ru: {
    name: "Русский",
    english: "Russian",
    frequencies: true,
    alphabet: "cyrillic",
    dictionaryURL: "https://en.openrussian.org/?search={{query}}",
    dictionaryCodes: { en: "en", ru: "ru" },
    wiktionary: true,
    flag: "🇷🇺",
    ttsVoiceSelectedText: "Здравствуйте, вы выбрали голос {{voiceName}}.",
  },
  ro: {
    name: "Română",
    english: "Romanian",
    frequencies: true,
    wiktionary: true,
    flag: "🇷🇴",
    ttsVoiceSelectedText: "Bună, ați selectat vocea {{voiceName}}.",
  },
  cs: {
    name: "Čeština",
    english: "Czech",
    frequencies: true,
    alphabet: "latin-ext",
    redditUrl:
      "http://www.reddit.com/r/ReadingCzech/submit?url={{publicUrl}}&title={{title}}",
    flag: "🇨🇿",
    ttsVoiceSelectedText: "Ahoj, vybrali jste hlas {{voiceName}}.",
  },
  el: {
    name: "Ελληνικά",
    english: "Greek",
    frequencies: true,
    alphabet: "greek",
    wiktionary: true,
    flag: "🇬🇷",
    ttsVoiceSelectedText: "Γεια σας, έχετε επιλέξει τη φωνή {{voiceName}}.",
  },
  tr: {
    name: "Türkçe",
    english: "Turkish",
    frequencies: true,
    alphabet: "latin-ext",
    wiktionary: true,
    flag: "🇹🇷",
    ttsVoiceSelectedText: "Merhaba, {{voiceName}} sesini seçtiniz.",
  },
  cy: {
    name: "Cymraeg",
    english: "Welsh",
    wiktionary: true,
    flag: "🏴",
    ttsVoiceSelectedText: "Helo, rydych wedi dewis y llais {{voiceName}}.",
  },
  nl: {
    name: "Nederlands",
    english: "Dutch",
    frequencies: true,
    dictionaryURL:
      "https://www.mijnwoordenboek.nl/vertaal/{{source}}/{{target}}/{{query}}",
    dictionaryCodes: {
      en: "EN",
      de: "DE",
      es: "ES",
      fr: "FR",
      nl: "NL",
    },
    monolingualDictionaryURL:
      "https://www.vandale.nl/opzoeken?pattern={{query}}&lang=nn#.Vgvo84-qpBc",
    alphabet: "latin-ext",
    starterStories: {
      easy: "648c8cece6ebfa9964a44143",
      medium: "648853a42d6cce87eb449a4d",
    },
    flag: "🇳🇱",
    ttsVoiceSelectedText: "Hallo, je hebt de stem {{voiceName}} geselecteerd.",
  },
  sl: {
    name: "Slovenski jezik",
    english: "Slovenian",
    flag: "🇸🇮",
    ttsVoiceSelectedText: "Pozdravljeni, izbrali ste glas {{voiceName}}.",
  },
  fi: {
    name: "Suomi",
    english: "Finnish",
    frequencies: true,
    wiktionary: true,
    flag: "🇫🇮",
    ttsVoiceSelectedText: "Hei, olet valinnut äänen {{voiceName}}.",
  },
  ar: {
    name: "العربية",
    english: "Arabic",
    direction: "RTL",
    frequencies: true,
    dictionaryURL: "http://www.aljazem.com/en/{{query}}",
    dictionaryCodes: { en: "en", ar: "ar" },
    flag: "🇸🇦",
    ttsVoiceSelectedText: "مرحبًا، لقد اخترت الصوت {{voiceName}}.",
  },
  iw: {
    name: "עברית",
    english: "Hebrew",
    direction: "RTL",
    dictionaryURL: "https://www.morfix.co.il/en/{{query}}",
    dictionaryCodes: { en: "en", iw: "iw" },
    frequencies: true,
    flag: "🇮🇱",
    ttsVoiceSelectedText: "שלום, בחרת את הקול {{voiceName}}.",
  },
  is: {
    name: "Íslenska",
    english: "Icelandic",
    flag: "🇮🇸",
    ttsVoiceSelectedText: "Halló, þú hefur valið röddina {{voiceName}}.",
  },
  no: {
    name: "Norsk",
    english: "Norwegian",
    frequencies: true,
    flag: "🇳🇴",
    ttsVoiceSelectedText: "Hei, du har valgt stemmen {{voiceName}}.",
  },
  gl: {
    name: "Galego",
    english: "Galician",
    flag: "🇪🇸",
    ttsVoiceSelectedText: "Ola, escolleches a voz {{voiceName}}.",
  },
  eu: {
    name: "Euskara",
    english: "Basque",
    frequencies: true,
    flag: "🇪🇸",
    ttsVoiceSelectedText: "Kaixo, {{voiceName}} ahotsa aukeratu duzu.",
  },
  la: {
    name: "Latina",
    english: "Latin",
    dictionaryURL: "https://www.latin-dictionary.net/search/latin/{{query}}",
    dictionaryCodes: { en: "en", fa: "la" },
    alphabet: "latin-ext",
    flag: "🏛️",
    ttsVoiceSelectedText: "Salve, vocem {{voiceName}} elegisti.",
  },
  bn: {
    name: "বাংলা",
    english: "Bengali",
    flag: "🇧🇩",
    ttsVoiceSelectedText: "হ্যালো, আপনি {{voiceName}} কণ্ঠটি নির্বাচন করেছেন।",
  },
  fa: {
    name: "فارسی",
    english: "Persian",
    direction: "RTL",
    frequencies: true,
    dictionaryURL: "https://www.farsi123.com/?word={{query}}",
    dictionaryCodes: { en: "eng", fa: "fa" },
    flag: "🇮🇷",
    ttsVoiceSelectedText: "سلام، شما صدای {{voiceName}} را انتخاب کرده‌اید.",
  },
  sk: {
    name: "Slovenský",
    english: "Slovak",
    frequencies: true,
    flag: "🇸🇰",
    ttsVoiceSelectedText: "Ahoj, vybrali ste hlas {{voiceName}}.",
  },
  hr: {
    name: "Hrvatski",
    english: "Croatian",
    frequencies: true,
    flag: "🇭🇷",
    ttsVoiceSelectedText: "Pozdrav, odabrali ste glas {{voiceName}}.",
  },
  bg: {
    name: "български",
    english: "Bulgarian",
    frequencies: true,
    alphabet: "cyrillic",
    wiktionary: true,
    flag: "🇧🇬",
    ttsVoiceSelectedText: "Здравейте, вие сте избрали гласа {{voiceName}}.",
  },
  uk: {
    name: "українська",
    english: "Ukrainian",
    frequencies: true,
    alphabet: "cyrillic",
    flag: "🇺🇦",
    ttsVoiceSelectedText: "Привіт, ви обрали голос {{voiceName}}.",
  },
  ko: {
    name: "한국어",
    english: "Korean",
    dictionaryURL: "https://dic.daum.net/search.do?q={{query}}&dic={{target}}",
    dictionaryCodes: { en: "eng", ko: "ko" },
    wiktionary: true,
    flag: "🇰🇷",
    ttsVoiceSelectedText: "안녕하세요, {{voiceName}} 음성을 선택하셨습니다.",
  },
  id: {
    name: "Indonesia",
    english: "Indonesian",
    frequencies: true,
    flag: "🇮🇩",
    ttsVoiceSelectedText: "Halo, Anda telah memilih suara {{voiceName}}.",
  },
  ja: {
    name: "日本語",
    english: "Japanese",
    dictionaryURL: "https://jisho.org/words?jap={{query}}&eng=&dict=edict",
    dictionaryCodes: { en: "en", ja: "ja" },
    noWordBoundaries: true,
    flag: "🇯🇵",
    transliterations: ["furigana", "romaji"],
    ttsVoiceSelectedText:
      "こんにちは、あなたは{{voiceName}}の声を選択しました。",
  },
  zh: {
    name: "中文",
    english: "Chinese",
    noWordBoundaries: true,
    dictionaryURL:
      "https://www.mdbg.net/chinese/dictionary?page=worddict&wdrst=0&wdqb={{query}}",
    dictionaryCodes: { en: "en", zh: "zh" },
    wiktionary: true,
    flag: "🇨🇳",
    transliterations: ["pinyin"],
    ttsVoiceSelectedText: "您好，您已选择语音 {{voiceName}}。",
  },
  "zh-TW": {
    name: "繁體中文",
    english: "Traditional Chinese",
    noWordBoundaries: true,
    firstLanguageOnly: true,
    dictionaryURL:
      "https://www.mdbg.net/chindict/chindict.php?page=worddict&wdrst=0&wdqb={{query}}",
    dictionaryCodes: { en: "en", "zh-TW": "zh" },
    flag: "🇹🇼",
    transliterations: ["pinyin"],
    ttsVoiceSelectedText: "您好，您已選擇聲音 {{voiceName}}。",
  },
  th: {
    name: "ภาษาไทย",
    english: "Thai",
    noWordBoundaries: true,
    dictionaryURL: "https://www.thai2english.com/search.aspx?q={{query}}",
    dictionaryCodes: { en: "en", hi: "th" },
    wiktionary: true,
    flag: "🇹🇭",
    ttsVoiceSelectedText: "สวัสดี คุณได้เลือกเสียง {{voiceName}} แล้ว",
  },
  ga: {
    name: "Gaeilge",
    english: "Irish",
    flag: "🇮🇪",
    ttsVoiceSelectedText:
      "Dia dhuit, tá an guth {{voiceName}} roghnaithe agat.",
  },
  ur: {
    name: "اردو",
    english: "Urdu",
    direction: "RTL",
    flag: "🇵🇰",
    ttsVoiceSelectedText: "ہیلو، آپ نے {{voiceName}} کی آواز کا انتخاب کیا ہے۔",
  },
  lt: {
    name: "Lietuvių kalba",
    english: "Lithuanian",
    frequencies: true,
    wiktionary: true,
    flag: "🇱🇹",
    ttsVoiceSelectedText: "Sveiki, jūs pasirinkote balsą {{voiceName}}.",
  },
  hi: {
    name: "हिंदी",
    english: "Hindi",
    dictionaryURL: "https://www.shabdkosh.com/hi/translate?e={{query}}&l=hi",
    dictionaryCodes: { en: "en", hi: "hi" },
    flag: "🇮🇳",
    ttsVoiceSelectedText: "नमस्ते, आपने आवाज़ {{voiceName}} को चुना है।",
  },
  af: {
    name: "Afrikaans",
    english: "Afrikaans",
    flag: "🇿🇦",
    ttsVoiceSelectedText: "Hallo, jy het die stem {{voiceName}} gekies.",
  },
  yi: {
    name: "ייִדיש",
    english: "Yiddish",
    direction: "RTL",
    flag: "🇮🇱",
    ttsVoiceSelectedText:
      "האַלאָ, איר האָט אויסגעקליבן די שטימע {{voiceName}}.",
  },
  vi: {
    name: "tiếng Việt",
    english: "Vietnamese",
    alphabet: "vietnamese",
    wiktionary: true,
    flag: "🇻🇳",
    ttsVoiceSelectedText: "Xin chào, bạn đã chọn giọng nói {{voiceName}}.",
  },
  et: {
    name: "Eesti",
    english: "Estonian",
    frequencies: true,
    wiktionary: true,
    monolingualDictionaryURL:
      "https://www.eki.ee/dict/psv/index.cgi?Q={{query}}",
    dictionaryURL: "https://www.eki.ee/dict/ies/index.cgi?&F=V&Q={{query}}",
    dictionaryCodes: { en: "en", et: "et" },
    flag: "🇪🇪",
    ttsVoiceSelectedText: "Tere, te olete valinud hääle {{voiceName}}.",
  },
  mt: {
    name: "Malti",
    english: "Maltese",
    flag: "🇲🇹",
    ttsVoiceSelectedText: "Bonġu, int għażilt il-leħen {{voiceName}}.",
  },
  hu: {
    name: "Magyar",
    english: "Hungarian",
    frequencies: true,
    dictionaryURL:
      "http://szotar.sztaki.hu/en/search?fromlang={{source}}&tolang={{target}}&searchWord={{query}}",
    dictionaryCodes: { en: "eng", hu: "hun" },
    wiktionary: true,
    flag: "🇭🇺",
    ttsVoiceSelectedText: "Helló, a {{voiceName}} hangot választotta.",
  },
  bs: {
    name: "Bosanski",
    english: "Bosnian",
    alphabet: "cyrillic",
    flag: "🇧🇦",
    ttsVoiceSelectedText: "Zdravo, odabrali ste glas {{voiceName}}.",
  },
  eo: {
    name: "Esperanto",
    english: "Esperanto",
    alphabet: "latin-ext",
    flag: "🟢",
    ttsVoiceSelectedText: "Saluton, vi elektis la voĉon {{voiceName}}.",
  },
  lv: {
    name: "Latviešu valoda",
    english: "Latvian",
    alphabet: "latin-ext",
    frequencies: true,
    dictionaryURL:
      "https://letonika.lv/groups/default.aspx?g=2&r=10621033&q={{query}}",
    dictionaryCodes: { en: "en", lv: "lv" },
    flag: "🇹🇭",
    ttsVoiceSelectedText: "Sveiki, jūs esat izvēlējies balsi {{voiceName}}.",
  },
  tl: {
    name: "Filipino",
    english: "Filipino",
    flag: "🇵🇭",
    ttsVoiceSelectedText: "Kamusta, pinili mo ang boses na {{voiceName}}.",
  },
  ht: {
    name: "Kreyòl ayisyen",
    english: "Haitian Creole",
    flag: "🇭🇹",
    ttsVoiceSelectedText: "Bonjou, ou te chwazi vwa {{voiceName}}.",
  },
  ml: {
    name: "മലയാളം",
    english: "Malayalam",
    wiktionary: true,
    flag: "🇮🇳",
    ttsVoiceSelectedText:
      "ഹലോ, നിങ്ങൾ {{voiceName}} ശബ്ദം തിരഞ്ഞെടുത്തിട്ടുണ്ട്.",
  },
  ka: {
    name: "ქართული ენა",
    english: "Georgian",
    dictionaryURL: "http://next.translate.ge/?#search/{{query}}",
    dictionaryCodes: { en: "en", ka: "ka" },
    flag: "🇬🇪",
    ttsVoiceSelectedText: "გამარჯობა, თქვენ აირჩიეთ ხმა {{voiceName}}.",
  },
  sw: {
    name: "Kiswahili",
    english: "Swahili",
    flag: "🇰🇪",
    ttsVoiceSelectedText: "Halo, umechagua sauti {{voiceName}}.",
  },
  hy: {
    name: "հայերեն",
    english: "Armenian",
    flag: "🇦🇲",
    ttsVoiceSelectedText: "Բարեւ, դուք ընտրել եք {{voiceName}} ձայնը:",
  },
  my: {
    name: "မြန်မာ",
    english: "Burmese",
    flag: "🇲🇲",
    ttsVoiceSelectedText:
      "ဟယ်လို၊ သင်သည် အသံ {{voiceName}} ကို ရွေးချယ်ထားပါသည်။",
  },
  mn: {
    name: "Монгол",
    english: "Mongolian",
    flag: "🇲🇳",
    ttsVoiceSelectedText:
      "Сайн байна уу, та {{voiceName}} дуу хоолойг сонголоо.",
  },
  mi: {
    name: "Maori",
    english: "Maori",
    flag: "🇳🇿",
    ttsVoiceSelectedText: "Kia ora, kua kōwhiria e koe te reo {{voiceName}}.",
  },
  be: {
    name: "Беларуская",
    english: "Belarusian",
    alphabet: "cyrillic",
    flag: "🇧🇾",
    ttsVoiceSelectedText: "Прывітанне, вы абралі голас {{voiceName}}.",
  },
  tt: {
    name: "Татарча",
    english: "Tatar",
    flag: "🇷🇺",
    ttsVoiceSelectedText: "Сәлам, сез {{voiceName}} тавышын сайлагансыз.",
  },
  sq: {
    name: "Shqip",
    english: "Albanian",
    flag: "🇦🇱",
    ttsVoiceSelectedText: "Përshëndetje, ju keni zgjedhur zërin {{voiceName}}.",
  },
  uz: {
    name: "O'zbek",
    english: "Uzbek",
    flag: "🇺🇿",
    ttsVoiceSelectedText: "Salom, siz {{voiceName}} ovozini tanladingiz.",
  },
  yo: {
    name: "Yoruba",
    english: "Yoruba",
    flag: "🇳🇬",
    ttsVoiceSelectedText: "Pẹ̀lú àṣírí, o ti yan ohùn {{voiceName}}.",
  },
  yue: {
    name: "粵語",
    english: "Cantonese",
    noWordBoundaries: true,
    flag: "🇭🇰",
    wiktionary: true,
    transliterations: ["Jyutping", "Yale"],
    ttsVoiceSelectedText: "你好，你已選擇了語音{{voiceName}}。",
  },
  kk: {
    name: "Қазақ",
    english: "Kazakh",
    flag: "🇰🇿",
    ttsVoiceSelectedText:
      "Сәлеметсіз бе, сіз {{voiceName}} дауысын таңдадыңыз.",
  },
  km: {
    name: "ភាសាខ្មែរ",
    english: "Khmer",
    flag: "🇰🇭",
    ttsVoiceSelectedText: "ជំរាបសួរ, អ្នកបានជ្រើសរើសសំឡេង {{voiceName}}។",
  },
  ky: {
    name: "Кыргызча",
    english: "Kyrgyz",
    flag: "🇰🇬",
    ttsVoiceSelectedText: "Саламатсызбы, сиз {{voiceName}} үнүн тандадыңыз.",
  },
  am: {
    name: "አማርኛ",
    english: "Amharic",
    flag: "🇪🇹",
    ttsVoiceSelectedText: "ሰላም፣ ድምፁን {{voiceName}} ይመርጡ።",
  },
  as: {
    name: "অসমীয়া",
    english: "Assamese",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "নমস্কাৰ, আপুনি {{voiceName}} কন্ঠ বাচনি কৰিছে।",
  },
  ay: {
    name: "Aymar aru",
    english: "Aymara",
    flag: "🇧🇴",
    ttsVoiceSelectedText: "Kamisaraki, {{voiceName}} ukan arupa ajllistawa.",
  },
  az: {
    name: "azərbaycan",
    english: "Azerbaijani",
    flag: "🇦🇿",
    ttsVoiceSelectedText: "Salam, siz {{voiceName}} səsini seçmisiniz.",
  },
  bm: {
    name: "bamanankan",
    english: "Bambara",
    flag: "🇲🇱",
    ttsVoiceSelectedText: "Fo, i ka boloko {{voiceName}} jìla.",
  },
  co: {
    name: "Corsu",
    english: "Corsican",
    flag: "🇫🇷",
    ttsVoiceSelectedText: "Salute, avete sceltu a voce {{voiceName}}.",
  },
  dv: {
    name: "ދިވެހި",
    english: "Dhivehi",
    flag: "🇲🇻",
    direction: "RTL",
    ttsVoiceSelectedText: "ހެލޯ، ތިބާ ހިމެނޭ ދެން {{voiceName}} ހުރިހާރު.",
  },
  ee: {
    name: "Eʋegbe",
    english: "Ewe",
    flag: "🇬🇭",
    ttsVoiceSelectedText: "Ame nye, wòle le {{voiceName}} gbɔ.",
  },
  fy: {
    name: "Frysk",
    english: "Frisian",
    flag: "🇳🇱",
    ttsVoiceSelectedText: "Hallo, jo hawwe de stim {{voiceName}} selektearre.",
  },
  gn: {
    name: "Avañe'ẽ",
    english: "Guarani",
    flag: "🇵🇾",
    ttsVoiceSelectedText: "Mba'éichapa, reiporavo {{voiceName}} ñe'ẽ.",
  },
  gu: {
    name: "ગુજરાતી",
    english: "Gujarati",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "હેલો, તમે {{voiceName}} અવાજ પસંદ કર્યો છે.",
  },
  ha: {
    name: "Hausa",
    english: "Hausa",
    flag: "🇳🇬",
    ttsVoiceSelectedText: "Sannu, ka zabi muryar {{voiceName}}.",
  },
  ig: {
    name: "Asụsụ Igbo",
    english: "Igbo",
    flag: "🇳🇬",
    ttsVoiceSelectedText: "Ndeewo, ị ahọpụtala olu {{voiceName}}.",
  },
  kn: {
    name: "ಕನ್ನಡ",
    english: "Kannada",
    flag: "🇮🇳",
    ttsVoiceSelectedText:
      "ಹಲೋ, ನೀವು {{voiceName}} ಧ್ವನಿಯನ್ನು ಆಯ್ಕೆ ಮಾಡಿರುವಿರಿ.",
  },
  rw: {
    name: "Ikinyarwanda",
    english: "Kinyarwanda",
    flag: "🇷🇼",
    ttsVoiceSelectedText: "Muraho, wahisemo ijwi {{voiceName}}.",
  },
  ku: {
    name: "Kurdî",
    english: "Kurdish (Kurmanji)",
    flag: "🇮🇶",
    ttsVoiceSelectedText: "Slav, tu dengê {{voiceName}} hilbijartî.",
  },
  lo: {
    name: "ລາວ",
    english: "Lao",
    flag: "🇱🇦",
    ttsVoiceSelectedText: "ສະບາຍດີ, ທ່ານໄດ້ເລືອກສຽງ {{voiceName}}.",
  },
  ln: {
    name: "Lingála",
    english: "Lingala",
    flag: "🇨🇩",
    ttsVoiceSelectedText: "Mbote, oponi mongongo {{voiceName}}.",
  },
  lg: {
    name: "Luganda",
    english: "Luganda",
    flag: "🇺🇬",
    ttsVoiceSelectedText: "Ki kati, olonze eddoboozi {{voiceName}}.",
  },
  lb: {
    name: "Lëtzebuergesch",
    english: "Luxembourgish",
    flag: "🇱🇺",
    ttsVoiceSelectedText: "Moien, Dir hutt d'Stëmm {{voiceName}} ausgewielt.",
  },
  mk: {
    name: "Македонски",
    english: "Macedonian",
    flag: "🇲🇰",
    ttsVoiceSelectedText: "Здраво, го одбравте гласот {{voiceName}}.",
  },
  mg: {
    name: "Malagasy",
    english: "Malagasy",
    flag: "🇲🇬",
    ttsVoiceSelectedText: "Salama, ianao dia nifidy ny feo {{voiceName}}.",
  },
  ms: {
    name: "Bahasa Melayu",
    english: "Malay",
    flag: "🇲🇾",
    ttsVoiceSelectedText: "Helo, anda telah memilih suara {{voiceName}}.",
  },
  mr: {
    name: "मराठी",
    english: "Marathi",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "नमस्कार, आपण {{voiceName}} या आवाजाचे चयन केले आहे.",
  },
  ne: {
    name: "नेपाली",
    english: "Nepali",
    flag: "🇳🇵",
    ttsVoiceSelectedText: "नमस्ते, तपाईंले स्वर {{voiceName}} चयन गर्नुभएको छ।",
  },
  om: {
    name: "Afaan Oromoo",
    english: "Oromo",
    flag: "🇪🇹",
    ttsVoiceSelectedText: "Akkam, sagalee {{voiceName}} filatteetta.",
  },
  ps: {
    name: "پښتو",
    english: "Pashto",
    flag: "🇦🇫",
    direction: "RTL",
    ttsVoiceSelectedText: "سلام، تاسو د غږ {{voiceName}} انتخاب کړی دی.",
  },
  pa: {
    name: "ਪੰਜਾਬੀ",
    english: "Punjabi",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "ਹੈਲੋ, ਤੁਸੀਂ ਅਵਾਜ਼ {{voiceName}} ਦਾ ਚੋਣ ਕੀਤਾ ਹੈ।",
  },
  qu: {
    name: "Runasimi",
    english: "Quechua",
    flag: "🇵🇪",
    ttsVoiceSelectedText:
      "Napaykullayki, {{voiceName}} sutiyuq yawarniyta akllawaykiki.",
  },
  sm: {
    name: "Gagana Samoa",
    english: "Samoan",
    flag: "🇼🇸",
    ttsVoiceSelectedText: "Talofa, ua e filifilia le leo {{voiceName}}.",
  },
  sa: {
    name: "संस्कृतम्",
    english: "Sanskrit",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "हरिः, त्वं {{voiceName}} इति स्वरं चयनं कृतवानसि।",
  },
  st: {
    name: "Sesotho",
    english: "Sesotho",
    flag: "🇱🇸",
    ttsVoiceSelectedText: "Lumela, u khethile lentsoe {{voiceName}}.",
  },
  sn: {
    name: "chiShona",
    english: "Shona",
    flag: "🇿🇼",
    ttsVoiceSelectedText: "Mhoro, wasarudza inzwi {{voiceName}}.",
  },
  sd: {
    name: "سنڌي",
    english: "Sindhi",
    flag: "🇵🇰",
    direction: "RTL",
    ttsVoiceSelectedText: "سلام، توهان {{voiceName}} آواز چونڊيو آهي.",
  },
  so: {
    name: "Soomaaliga",
    english: "Somali",
    flag: "🇸🇴",
    ttsVoiceSelectedText: "Hello, waxaad dooratay codka {{voiceName}}.",
  },
  su: {
    name: "Basa Sunda",
    english: "Sundanese",
    flag: "🇮🇩",
    ttsVoiceSelectedText: "Halo, anjeun parantos milih sora {{voiceName}}.",
  },
  tg: {
    name: "тоҷикӣ",
    english: "Tajik",
    flag: "🇹🇯",
    ttsVoiceSelectedText: "Салом, шумо овози {{voiceName}}-ро интихоб кардед.",
  },
  ta: {
    name: "தமிழ்",
    english: "Tamil",
    flag: "🇮🇳",
    ttsVoiceSelectedText:
      "வணக்கம், நீங்கள் {{voiceName}} குரலை தேர்ந்தெடுத்துள்ளீர்கள்.",
  },
  te: {
    name: "తెలుగు",
    english: "Telugu",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "హలో, మీరు {{voiceName}} వాయిస్‌ను ఎంచుకున్నారు.",
  },
  ti: {
    name: "ትግርኛ",
    english: "Tigrinya",
    flag: "🇪🇷",
    ttsVoiceSelectedText: "ሰላም ፣ ድምፂ ዝመርጽካዮ እዩ {{voiceName}}።",
  },
  ts: {
    name: "Xitsonga",
    english: "Tsonga",
    flag: "🇿🇦",
    ttsVoiceSelectedText: "Xewani, u hlawurile rito ra {{voiceName}}.",
  },
  tk: {
    name: "Türkmen",
    english: "Turkmen",
    flag: "🇹🇲",
    ttsVoiceSelectedText: "Salam, siz {{voiceName}} sesini saýladyňyz.",
  },
  ug: {
    name: "ئۇيغۇرچە",
    english: "Uyghur",
    flag: "🇨🇳",
    direction: "RTL",
    ttsVoiceSelectedText: "ياخشىمۇسىز ، سىز {{voiceName}} ئاۋازىنى تاللىدىڭىز.",
  },
  xh: {
    name: "isiXhosa",
    english: "Xhosa",
    flag: "🇿🇦",
    ttsVoiceSelectedText: "Molo, ukhethe ilizwi {{voiceName}}.",
  },
  zu: {
    name: "isiZulu",
    english: "Zulu",
    flag: "🇿🇦",
    ttsVoiceSelectedText: "Sawubona, ukhethe izwi elithi {{voiceName}}.",
  },
  jv: {
    name: "ꦧꦱꦗꦮ",
    english: "Javanese",
    flag: "🇮🇩",
    ttsVoiceSelectedText: "Halo, sampeyan milih swara {{voiceName}}.",
  },
  ny: {
    name: "Chichewa",
    english: "Nyanja / Chichewa",
    flag: "🇲🇼",
    ttsVoiceSelectedText: "Moni, mwasankha mawu {{voiceName}}.",
  },
  or: {
    name: "ଓଡ଼ିଆ",
    english: "Odia / Oriya",
    flag: "🇮🇳",
    ttsVoiceSelectedText: "ନମସ୍କାର, ଆପଣ {{voiceName}} କଣ୍ଠସ୍ୱର ଚୟନ କରିଛନ୍ତି।",
  },
  gd: {
    name: "Gàidhlig",
    english: "Scots Gaelic",
    flag: "🏴",
    ttsVoiceSelectedText:
      "Halò, tha thu air a’ ghuth {{voiceName}} a thaghadh.",
  },
  si: {
    name: "සිංහල",
    english: "Sinhala / Sinhalese",
    flag: "🇱🇰",
    ttsVoiceSelectedText:
      "හෙලෝ, ඔබ {{voiceName}} හදින් ඉව වෝයිස් එක තෝරාගෙන තිබේ.",
  },
  ak: {
    name: "Twi",
    english: "Twi / Akan",
    flag: "🇬🇭",
    ttsVoiceSelectedText: "Hello, wo ayi nne {{voiceName}}.",
  },
  ckb: {
    name: "سۆرانی",
    english: "Kurdish (Sorani)",
    direction: "RTL",
    flag: "🇮🇶",
    ttsVoiceSelectedText: "سڵاو، تۆ دەنگی {{voiceName}} هەڵبژاردووە.",
  },
  ceb: {
    name: "Bisaya",
    english: "Cebuano",
    flag: "🇵🇭",
    ttsVoiceSelectedText: "Kumusta, gipili nimo ang tingog nga {{voiceName}}.",
  },
};

const wordReferenceLanguages = [
  // "es",
  // "fr",
  // "it",
  // "de",
  // "pt",
  // "pl",
  // "ro",
  // "cs",
  // "el",
  // "tr",
  // "ja",
  // "zh",
  // "ru",
];

_.each(map, (lang, code) => {
  if (
    !lang.frequencies ||
    lang.noWordBoundaries ||
    lang.direction === "RTL" ||
    (!_.contains(wordReferenceLanguages, code) && !lang.dictionaryURL)
  ) {
    lang.beta = true;
  }
});

delete map.en.beta;

const wordReferenceCode = (code) => {
  if (code === "el") {
    return "gr";
  } else if (code === "cs") {
    return "cz";
  }
  return code;
};

export const list = utils.sortBy(
  _.map(map, (info, code) => _.extend(info, { code: code })),
  { english: 1 }
);

export const learningMap = {};
_.each(map, (info, code) => {
  if (!info.firstLanguageOnly) {
    learningMap[code] = info;
  }
});

export const learningList = utils.sortBy(
  _.filter(list, (info) => !info.firstLanguageOnly),
  { english: 1 }
);

// var wordReferenceSupported = function (source, target) {
//   if (
//     (source === "en" && wordReferenceLanguages.indexOf(target) !== -1) ||
//     (target === "en" && wordReferenceLanguages.indexOf(source) !== -1)
//   ) {
//     return true;
//   }
//   return false;
// };

export const languageSupportsChat = (code) => {
  const language = map[code];
  return language && !language.noWordBoundaries && language.direction !== "RTL";
};

const getWordReferenceCodes = (source, target) => {
  const result = {};

  if (wordReferenceLanguages.indexOf(source) === -1 && source !== "en") {
    // can't do anything
    return {};
  }

  if (source !== "en" && target !== "en") {
    target = "en";
    result.targetLanguageCode = "en";
  } else if (wordReferenceLanguages.indexOf(target) !== -1 || target === "en") {
    result.targetLanguageCode = target;
  } else {
    return {};
  }

  result.source = wordReferenceCode(source);
  result.target = wordReferenceCode(target);

  return result;
};

const wiktionaryURLTemplate = (firstLanguage, learningLanguage) =>
  `https://${firstLanguage}.m.wiktionary.org/wiki/{{query}}${
    learningLanguage ? `#${map[learningLanguage].english}` : ""
  }`;

// gets a template where {{query}} needs to be replaced with the word/phrase to lookup to be
// complete
export const getEmbeddedDictionaryTemplate = (source, target, user) => {
  const result = {};
  const wordReferenceLanguageCodes = getWordReferenceCodes(source, target);
  const customDictionaries = user && user.get("customDictionaries");
  let urlTarget;
  let urlSource;

  if (source === target) {
    // monolingual mode
    if (customDictionaries && customDictionaries[source + "-mono"]) {
      result.urlTemplate = customDictionaries[source + "-mono"];
    } else if (map[source].monolingualDictionaryURL) {
      result.urlTemplate = map[source].monolingualDictionaryURL;
    } else if (map[source].wiktionary) {
      result.urlTemplate = wiktionaryURLTemplate(source);
    }
  } else if (customDictionaries && customDictionaries[source]) {
    result.urlTemplate = customDictionaries[source];
  } else if (map[source].dictionaryURL) {
    urlTarget = map[source].dictionaryCodes[target];
    urlSource = map[source].dictionaryCodes[source];

    if (urlTarget) {
      result.target = target;
    } else {
      const pair = _.first(_.pairs(map[source].dictionaryCodes));

      result.target = pair[0];
      urlTarget = pair[1];
    }

    result.urlTemplate = map[source].dictionaryURL
      .replace(/{{source}}/g, encodeURIComponent(urlSource))
      .replace(/{{target}}/g, encodeURIComponent(urlTarget));
  } else if (map[target].dictionaryURL) {
    urlTarget = map[target].dictionaryCodes[target];
    urlSource = map[target].dictionaryCodes[source];

    if (urlSource) {
      result.target = target;

      result.urlTemplate = map[target].dictionaryURL
        .replace(/{{source}}/g, encodeURIComponent(urlSource))
        .replace(/{{target}}/g, encodeURIComponent(urlTarget));
    }
  } else if (wordReferenceLanguageCodes.source) {
    result.urlTemplate =
      "https://www.wordreference.com/" +
      wordReferenceLanguageCodes.source +
      wordReferenceLanguageCodes.target +
      "/{{query}}";
    result.target = wordReferenceLanguageCodes.targetLanguageCode;
  } else if (map[target].wiktionary) {
    result.urlTemplate = wiktionaryURLTemplate(target, source);
    result.target = target;
  }

  return result;
};

export const getEmbeddedDictionary = (source, target, query, context, user) => {
  /** @type {any} */
  const result = getEmbeddedDictionaryTemplate(source, target, user);

  if (result.urlTemplate) {
    result.URL = result.urlTemplate
      .replace(/{{query}}/g, encodeURIComponent(query))
      .replace(/{{context}}/g, encodeURIComponent(context));

    // sandboxed iframe to prevent focus stealing
    result.html =
      '<iframe sandbox="allow-forms allow-scripts allow-same-origin" id=wordReferenceIFrame src="' +
      result.URL +
      '"></iframe>';

    if (typeof window !== "undefined") {
      if (window.location && window.location.protocol === "https:") {
        if (
          /^http:/.test(result.URL) &&
          // @ts-ignore
          !window.READLANG.httpsWarningDismissed
        ) {
          result.html =
            '<div class="dictionaryWarning">' +
            '<div class="closeButton" onclick="$(this).parent().remove();window.READLANG.httpsWarningDismissed=true;">&nbsp;x&nbsp;</div>' +
            "<p>If the dictionary doesn't appear, it's probably because your browser is preventing " +
            "unsecure content appearing on this page.</p>" +
            '<p>Either click the "Open in new window" button above' +
            ' or allow the "unsafe" dictionary content as follows...' +
            "</p>" +
            "<h3>Chrome:</h3>" +
            '<img src="/content/chromeMixedContent.png"/>' +
            "<h3>Firefox:</h3>" +
            '<img src="/content/firefoxMixedContent.png"/>' +
            "</div>" +
            result.html;
        }
      }
    }
  } else {
    result.html =
      "<p>Readlang doesn't have a " +
      (source === target
        ? "monolingual dictionary for " + map[source].english
        : "dictionary for " +
          map[source].english +
          " to " +
          map[target].english) +
      ".</p>" +
      '<p><strong>Add a dictionary yourself</strong> by following these instructions: <a target="_blank" href="https://forum.readlang.com/t/change-the-dictionary/52">Add a custom dictionary</a>.</p>';
  }

  return result;
};

const flagLanguageCodes = [
  "es",
  "de",
  "en",
  "fr",
  "it",
  "ru",
  "nl",
  "pl",
  "pt",
  "ko",
  "sv",
  "el",
  "no",
];

export const flagList = _.map(flagLanguageCodes, (code) => ({
  ...map[code],
  code,
}));
